import clsx from "clsx";
import { FC, useCallback, useLayoutEffect, useRef, useState } from "react";

import { useResizeObserver } from "@tsmono/react/hooks";

import { EventNav } from "./EventNav";
import styles from "./EventNavs.module.css";
import { EventNavsPicker } from "./EventNavsPicker";

interface EventNavsProps {
  navs: Array<{ id: string; title: string; target: string }>;
  selectedNav: string;
  setSelectedNav: (target: string) => void;
}

// Hysteresis (px) so a measurement landing right at the threshold doesn't
// flip modes on every resize tick.
const SWITCH_TOLERANCE = 4;

/**
 * Renders the event panel's tab navigation. When the available width is
 * smaller than the natural width of the pill row, collapses to a picker
 * dropdown instead of letting the pills wrap to a second line.
 */
export const EventNavs: FC<EventNavsProps> = ({
  navs,
  selectedNav,
  setSelectedNav,
}) => {
  const [mode, setMode] = useState<"tabs" | "picker">("tabs");
  const [labelWidth, setLabelWidth] = useState(0);
  const probeRef = useRef<HTMLUListElement>(null);
  const naturalWidthRef = useRef(0);

  // Re-measure whenever the nav set changes — titles, count, or order all
  // affect the natural single-line width that drives the mode switch and the
  // picker label's natural width.
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useLayoutEffect(() => {
    const probe = probeRef.current;
    if (!probe) return;
    naturalWidthRef.current = probe.scrollWidth;
    // Measure each title's text width via canvas, using the font resolved on
    // a real probe pill so the result honors `text-style-label` (uppercase)
    // and `text-size-small`. Canvas avoids the layout/visibility quirks that
    // can make DOM-based measurements come back as 0.
    const sample = probe.querySelector<HTMLElement>(".nav-link");
    if (!sample) return;
    const cs = getComputedStyle(sample);
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return;
    ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const transform = cs.textTransform;
    const transformed = (s: string) =>
      transform === "uppercase"
        ? s.toUpperCase()
        : transform === "lowercase"
          ? s.toLowerCase()
          : s;
    let max = 0;
    navs.forEach((nav) => {
      max = Math.max(max, ctx.measureText(transformed(nav.title)).width);
    });
    setLabelWidth(Math.ceil(max));
  }, [navs]);

  const handleResize = useCallback((entry: ResizeObserverEntry) => {
    const available = entry.contentRect.width;
    const natural = naturalWidthRef.current;
    if (natural === 0) return;
    setMode((current) => {
      if (current === "tabs" && available < natural - SWITCH_TOLERANCE) {
        return "picker";
      }
      if (current === "picker" && available >= natural + SWITCH_TOLERANCE) {
        return "tabs";
      }
      return current;
    });
  }, []);

  const containerRef = useResizeObserver(handleResize);

  return (
    <div ref={containerRef} className={styles.wrapper}>
      {mode === "tabs" ? (
        <ul
          className={clsx("nav", "nav-pills", styles.navs)}
          role="tablist"
          aria-orientation="horizontal"
        >
          {navs.map((nav) => (
            <EventNav
              key={nav.title}
              target={nav.target}
              title={nav.title}
              selectedNav={selectedNav}
              setSelectedNav={setSelectedNav}
            />
          ))}
        </ul>
      ) : (
        <EventNavsPicker
          navs={navs}
          selectedNav={selectedNav}
          setSelectedNav={setSelectedNav}
          labelWidth={labelWidth}
        />
      )}
      <ul
        ref={probeRef}
        aria-hidden
        className={clsx("nav", "nav-pills", styles.navs, styles.probe)}
      >
        {navs.map((nav) => (
          <li key={nav.id} className="nav-item">
            {/* Match EventNav's <button> element/classes so the probe's
                scrollWidth equals the real pill row's natural width. */}
            <button
              type="button"
              tabIndex={-1}
              className={clsx(
                "nav-link",
                "text-style-label",
                "text-size-small",
                styles.probeItem
              )}
            >
              {nav.title}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};
